import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSelector, useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { useDispatchPromise } from '../../utils/AppUtils';
import {
    exchangeMetaOAuth,
    getInstagramAutomationOAuthUrl,
    loadPostAutomationContext,
    publishSocialPostFlow,
} from '../../redux/socialPosts/action';
import {
    selectConnectedPlatformUsernames,
    selectConnectedPlatforms,
    selectInstagramAutomationFeatureEnabled,
    selectInstagramAutomationReady,
    selectSocialPostsLoadingContext,
} from '../../redux/socialPosts/selector';
import { getGlobalConfig } from '../../redux/configuration/action';
import { RootState } from '../../redux/root-reducer';
import { isProjectProcessingSelector, mediaForgeMessageSelector } from '../../selectors/selectors';
import type {
    CreateSocialPostRequest,
    InstagramAutomationInput,
    SocialPlatform,
    SocialPostWithAutomation,
    StandaloneMediaInput,
    ConnectedPlatformRow,
} from '../../types/instagramAutomation.types';
import { standaloneMediaTypeForPlatform } from '../../types/instagramAutomation.types';
import { openMetaOAuthPopup } from './openMetaOAuthPopup';
import { PostAutomationPlatformCard } from './PostAutomationPlatformCard';
import { brandAuthUrl } from '../../configurations/api/url';
import {
    applyTiktokToCreateBody,
    applyYoutubeToCreateBody,
    defaultTiktokFields,
    defaultYoutubeFields,
    type TiktokPostFields,
    type YoutubePostFields,
} from './platformPostFields';
import { validateTiktokPostFields } from '../../utils/tiktokDirectPostUx';
import {
    IG_AUTO_BG,
    IG_AUTO_BORDER,
    IG_AUTO_GOLD,
    IG_AUTO_MINT,
    IG_AUTO_MUTED,
    IG_AUTO_RED,
    IG_AUTO_WHITE,
    igAutoGlassCardSx,
    igAutoTextFieldSx,
} from './instagramAutomationTheme';
import { usePlanEntitlementGates } from '../../hook/usePlanEntitlementGates';
import { brandSocialLogin } from '../../redux/buzzbridge/actions';
import { connectedPlatformsUrl } from '../../configurations/api/url';
import { needsFacebookUpgradeForInstagram } from '../../utils/instagramOAuthProvider';
import type { PostAutomationDraft } from '../../constants/postAutomationIntent';
import { clearPostAutomationIntent, getPostAutomationDraft } from '../../constants/postAutomationIntent';
import axios from 'axios';

const PLATFORMS: SocialPlatform[] = ['instagram', 'tiktok', 'youtube'];
const DEFAULT_PUBLIC_REPLY = 'Thanks! Check your DMs.';

type PlatformPublishState = {
    status: 'idle' | 'working' | 'done' | 'failed';
    error?: string;
    postId?: string;
    platformPostUrl?: string;
};

export interface InstagramPostAutomationModalProps {
    open: boolean;
    onClose: () => void;
    /** Campaign mode — uses submission_id */
    submissionId?: string;
    /** Workshop / standalone mode — uses media_url + media_type */
    standaloneMedia?: StandaloneMediaInput;
    projectTitle?: string;
    defaultCaption?: string;
    onAutomationStatusChange?: (active: boolean) => void;
    /** Direct-hire brand upload — use Brand OAuth, not influencer. */
    brandId?: string;
    /** Prefill from Create Project draft; when autoPublishWhenReady, publish as soon as media is ready. */
    initialDraft?: PostAutomationDraft | null;
    autoPublishWhenReady?: boolean;
}

export function InstagramPostAutomationModal({
    open,
    onClose,
    submissionId,
    standaloneMedia,
    projectTitle,
    defaultCaption = '',
    onAutomationStatusChange,
    brandId,
    initialDraft,
    autoPublishWhenReady = false,
}: InstagramPostAutomationModalProps) {
    const { device } = useThemeContext();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const token = useSelector((s: RootState) => s.authentication.token);
    const loading = useSelector(selectSocialPostsLoadingContext);
    const influencerConnectedPlatforms = useSelector(selectConnectedPlatformUsernames);
    const influencerConnectedRows = useSelector(selectConnectedPlatforms);
    const instagramReady = useSelector(selectInstagramAutomationReady);
    const instagramCommentAutomationEnabled = useSelector(selectInstagramAutomationFeatureEnabled);
    const { hasInstagramAutomation, requireInstagramAutomationEntitlement } = usePlanEntitlementGates();
    const isMediaUploading = useSelector(isProjectProcessingSelector);
    const { progress: uploadProgress, uploadMessage } = useSelector(mediaForgeMessageSelector);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [brandUsernames, setBrandUsernames] = useState<Record<SocialPlatform, string | undefined>>({
        instagram: undefined,
        tiktok: undefined,
        youtube: undefined,
    });
    const [brandRows, setBrandRows] = useState<ConnectedPlatformRow[]>([]);
    const [brandLoading, setBrandLoading] = useState(false);

    const [caption, setCaption] = useState(defaultCaption);
    const [youtubeFields, setYoutubeFields] = useState<YoutubePostFields>(() =>
        defaultYoutubeFields(projectTitle, defaultCaption),
    );
    const [tiktokFields, setTiktokFields] = useState<TiktokPostFields>(() => defaultTiktokFields());
    const [platformEnabled, setPlatformEnabled] = useState<Record<SocialPlatform, boolean>>({
        instagram: true,
        tiktok: true,
        youtube: true,
    });
    const [platformPublish, setPlatformPublish] = useState<Record<SocialPlatform, PlatformPublishState>>({
        instagram: { status: 'idle' },
        tiktok: { status: 'idle' },
        youtube: { status: 'idle' },
    });

    const [automationEnabled, setAutomationEnabled] = useState(false);
    const [triggerWord, setTriggerWord] = useState('');
    const [dmMessage, setDmMessage] = useState('');
    const [publicReply, setPublicReply] = useState(DEFAULT_PUBLIC_REPLY);

    const [scheduleMode, setScheduleMode] = useState<'now' | 'schedule'>('now');
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');

    const platformEnabledInitRef = useRef(false);
    const [contextReady, setContextReady] = useState(false);
    const autoPublishFiredRef = useRef(false);
    const draftAppliedRef = useRef(false);

    const canPost = Boolean(submissionId || standaloneMedia?.media_url);
    const isStandalone = Boolean(standaloneMedia?.media_url);
    const isBrandPublish = Boolean(brandId);

    // Apply Create Project draft once per open.
    useEffect(() => {
        if (!open) {
            draftAppliedRef.current = false;
            autoPublishFiredRef.current = false;
            return;
        }
        if (draftAppliedRef.current) return;
        const draft = initialDraft ?? getPostAutomationDraft();
        if (!draft) return;
        draftAppliedRef.current = true;
        setPlatformEnabled(prev => ({
            instagram: draft.platforms?.instagram ?? prev.instagram,
            tiktok: draft.platforms?.tiktok ?? prev.tiktok,
            youtube: draft.platforms?.youtube ?? prev.youtube,
        }));
        setScheduleMode(draft.scheduleMode === 'schedule' ? 'schedule' : 'now');
        if (draft.scheduleMode === 'schedule' && draft.scheduledAt) {
            const d = new Date(draft.scheduledAt);
            if (!Number.isNaN(d.getTime())) {
                setScheduleDate(d.toISOString().slice(0, 10));
                setScheduleTime(d.toTimeString().slice(0, 5));
            }
        }
    }, [open, initialDraft]);

    // Auto-publish when media is ready and Create Project asked for immediate activation.
    useEffect(() => {
        if (!open || !autoPublishWhenReady || !canPost || saving || autoPublishFiredRef.current) return;
        if (isMediaUploading) return;
        autoPublishFiredRef.current = true;
        void (async () => {
            await handleActivate();
            clearPostAutomationIntent();
        })();
        // handleActivate is recreated often; fire once via ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, autoPublishWhenReady, canPost, isMediaUploading, saving]);

    const connectedPlatforms = isBrandPublish ? brandUsernames : influencerConnectedPlatforms;
    const connectedRows = isBrandPublish ? brandRows : influencerConnectedRows;
    const instagramRow = connectedRows.find(r => r.platform === 'instagram');
    const connectedPlatformRows = isBrandPublish ? brandRows : influencerConnectedRows;

    const loadBrandConnections = useCallback(async () => {
        if (!brandId || !token) return;
        setBrandLoading(true);
        try {
            const res = await axios.get(connectedPlatformsUrl, {
                params: { brand_id: brandId },
                headers: { Authorization: `Bearer ${token}` },
            });
            const platforms: ConnectedPlatformRow[] = res.data?.data?.platforms ?? res.data?.platforms ?? [];
            setBrandRows(platforms);
            const next: Record<SocialPlatform, string | undefined> = {
                instagram: undefined,
                tiktok: undefined,
                youtube: undefined,
            };
            for (const row of platforms) {
                const p = row.platform as SocialPlatform;
                if (p === 'instagram' || p === 'tiktok' || p === 'youtube') {
                    next[p] = row.platform_username || 'connected';
                }
            }
            setBrandUsernames(next);
        } catch {
            setBrandRows([]);
            setBrandUsernames({
                instagram: undefined,
                tiktok: undefined,
                youtube: undefined,
            });
        } finally {
            setBrandLoading(false);
        }
    }, [brandId, token]);

    const scheduledAtIso = useMemo(() => {
        if (scheduleMode !== 'schedule' || !scheduleDate || !scheduleTime) return undefined;
        const d = new Date(`${scheduleDate}T${scheduleTime}`);
        return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    }, [scheduleMode, scheduleDate, scheduleTime]);

    const loadContext = useCallback(
        (options?: { skipRefresh?: boolean; silent?: boolean }) => {
            if (!open || !token) return Promise.resolve();
            setError('');
            const brandLoad = brandId ? loadBrandConnections() : Promise.resolve();
            const influencerLoad = brandId
                ? Promise.resolve()
                : dispatchPromise({
                      type: loadPostAutomationContext.type,
                      payload: { skipRefresh: options?.skipRefresh, silent: options?.silent },
                  }).catch((err: unknown) => {
                      const msg =
                          (err as { data?: { message?: string } })?.data?.message ??
                          (err instanceof Error ? err.message : 'Unable to load post automation.');
                      setError(msg);
                  });
            return Promise.all([brandLoad, influencerLoad]).then(() => undefined);
        },
        [open, token, dispatchPromise, brandId, loadBrandConnections],
    );

    const refreshConnections = useCallback(
        () => (brandId ? loadBrandConnections() : loadContext({ skipRefresh: true, silent: true })),
        [brandId, loadBrandConnections, loadContext],
    );

    const handleBrandOAuth = useCallback(
        async (code: string, provider: string) => {
            if (!brandId) return;
            await dispatchPromise({
                type: brandSocialLogin.type,
                payload: { brand_id: brandId, code, provider },
            });
            await loadBrandConnections();
        },
        [brandId, dispatchPromise, loadBrandConnections],
    );

    useEffect(() => {
        if (open) {
            dispatch(getGlobalConfig());
            platformEnabledInitRef.current = false;
            setContextReady(false);
            setCaption(defaultCaption);
            setYoutubeFields(defaultYoutubeFields(projectTitle, defaultCaption));
            setTiktokFields(defaultTiktokFields());
            setAutomationEnabled(instagramCommentAutomationEnabled && hasInstagramAutomation);
            setPlatformPublish({
                instagram: { status: 'idle' },
                tiktok: { status: 'idle' },
                youtube: { status: 'idle' },
            });
            loadContext();
        }
    }, [
        open,
        defaultCaption,
        projectTitle,
        loadContext,
        dispatch,
        instagramCommentAutomationEnabled,
        hasInstagramAutomation,
    ]);

    useEffect(() => {
        if (!open || loading || brandLoading) return;
        setContextReady(true);
    }, [open, loading, brandLoading]);

    useEffect(() => {
        if (!open || loading || brandLoading || platformEnabledInitRef.current) return;
        platformEnabledInitRef.current = true;
        setPlatformEnabled({
            instagram: Boolean(connectedPlatforms.instagram),
            tiktok: Boolean(connectedPlatforms.tiktok),
            youtube: Boolean(connectedPlatforms.youtube),
        });
    }, [
        open,
        loading,
        brandLoading,
        connectedPlatforms.instagram,
        connectedPlatforms.tiktok,
        connectedPlatforms.youtube,
    ]);

    useEffect(() => {
        if (open) {
            setAutomationEnabled(instagramCommentAutomationEnabled && hasInstagramAutomation);
        }
    }, [open, instagramCommentAutomationEnabled, hasInstagramAutomation]);

    const showInitialLoader = (loading || brandLoading) && !contextReady;

    const connectInstagramAutomation = async () => {
        if (!token) return;
        setSaving(true);
        setError('');
        try {
            const url = await dispatchPromise({ type: getInstagramAutomationOAuthUrl.type });
            const code = await openMetaOAuthPopup(url as string);
            await dispatchPromise({ type: exchangeMetaOAuth.type, payload: { code } });
            await loadContext({ skipRefresh: true, silent: true });
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to enable comment automation.');
        } finally {
            setSaving(false);
        }
    };

    const buildAutomationPayload = (): InstagramAutomationInput | undefined => {
        if (!automationEnabled || !instagramCommentAutomationEnabled) return undefined;
        if (!instagramReady) return undefined;
        return {
            enabled: true,
            trigger_word: triggerWord.trim(),
            dm_message: dmMessage.trim(),
            public_reply_text: publicReply.trim() || DEFAULT_PUBLIC_REPLY,
        };
    };

    const tiktokConnectedRow = connectedPlatformRows.find(p => p.platform === 'tiktok');

    const validateForm = () => {
        if (!caption.trim() && !projectTitle?.trim() && !youtubeFields.title.trim()) {
            setError('Add a caption or YouTube title for your posts.');
            return false;
        }
        if (platformEnabled.youtube && connectedPlatforms.youtube && !youtubeFields.title.trim()) {
            setError('YouTube requires a title.');
            return false;
        }
        if (scheduleMode === 'schedule' && !scheduledAtIso) {
            setError('Pick a valid schedule date and time.');
            return false;
        }
        const selected = PLATFORMS.filter(p => platformEnabled[p] && connectedPlatforms[p]);
        if (!selected.length) {
            setError('Connect and enable at least one platform.');
            return false;
        }
        if (automationEnabled && platformEnabled.instagram && instagramCommentAutomationEnabled) {
            if (!triggerWord.trim()) {
                setError('Set the exact trigger comment for Instagram automation (e.g. LINK).');
                return false;
            }
            if (!dmMessage.trim()) {
                setError('DM message is required when Instagram automation is enabled.');
                return false;
            }
            if (!instagramReady) {
                setError('Enable comment automation permissions for Instagram, or turn off automation.');
                return false;
            }
        }
        if (platformEnabled.tiktok && connectedPlatforms.tiktok) {
            const tiktokValidation = validateTiktokPostFields(tiktokFields);
            if (!tiktokValidation.valid) {
                setError(tiktokValidation.message ?? 'Complete TikTok posting settings.');
                return false;
            }
        }
        return true;
    };

    const buildCreateBody = (platform: SocialPlatform): CreateSocialPostRequest => {
        let body: CreateSocialPostRequest = {
            platform,
            caption: caption.trim() || youtubeFields.title.trim() || projectTitle?.trim() || '',
            ...(scheduledAtIso ? { scheduled_at: scheduledAtIso } : {}),
        };

        if (platform === 'youtube') {
            body = applyYoutubeToCreateBody(body, youtubeFields);
        }

        if (platform === 'tiktok') {
            body = applyTiktokToCreateBody(body, tiktokFields);
        }

        if (isStandalone && standaloneMedia) {
            body.standalone = true;
            body.media_url = standaloneMedia.media_url;
            body.media_type = standaloneMediaTypeForPlatform(platform, standaloneMedia.media_type);
        } else if (submissionId) {
            body.submission_id = submissionId;
        }

        if (brandId) {
            body.brand_id = brandId;
        }

        if (platform === 'instagram') {
            const automation = buildAutomationPayload();
            if (automation) {
                body.instagram_automation = automation;
            }
        }

        return body;
    };

    const publishOnePlatform = async (platform: SocialPlatform) => {
        if (!token) return;

        setPlatformPublish(prev => ({ ...prev, [platform]: { status: 'working' } }));

        try {
            const body = buildCreateBody(platform);
            const automation =
                platform === 'instagram' && body.instagram_automation ? body.instagram_automation : undefined;
            const post = (await dispatchPromise({
                type: publishSocialPostFlow.type,
                payload: { body, configureAutomation: automation },
            })) as SocialPostWithAutomation;

            const igActive = platform === 'instagram' && post.instagram_automation?.status === 'active';
            if (igActive) {
                onAutomationStatusChange?.(true);
            }

            setPlatformPublish(prev => ({
                ...prev,
                [platform]: {
                    status: 'done',
                    postId: post._id,
                    platformPostUrl: post.platform_post_url,
                },
            }));
        } catch (err: unknown) {
            const msg =
                (err as { data?: { message?: string } })?.data?.message ??
                (err instanceof Error ? err.message : `Failed on ${platform}.`);
            setPlatformPublish(prev => ({
                ...prev,
                [platform]: { status: 'failed', error: msg },
            }));
            throw err;
        }
    };

    const handleActivate = async () => {
        if (!token || !canPost || saving) return;
        if (!validateForm()) return;

        setSaving(true);
        setError('');

        const targets = PLATFORMS.filter(p => platformEnabled[p] && connectedPlatforms[p]);
        const failures: string[] = [];

        for (const platform of targets) {
            try {
                await publishOnePlatform(platform);
            } catch {
                failures.push(platform);
            }
        }

        if (failures.length === targets.length) {
            setError('Could not publish to any platform. Check errors below.');
        } else if (failures.length) {
            setError(`Some platforms failed: ${failures.join(', ')}.`);
        }

        setSaving(false);
    };

    const dialogPaperSx = device.isMobile
        ? {
              bgcolor: IG_AUTO_BG,
              backgroundImage: 'none',
              borderRadius: 0,
              m: 0,
              width: '100%',
              maxWidth: '100%',
              height: '100dvh',
              maxHeight: '100dvh',
          }
        : {
              bgcolor: IG_AUTO_BG,
              backgroundImage: 'none',
              borderRadius: 3,
              border: `1px solid ${IG_AUTO_BORDER}`,
              m: 2,
              maxHeight: '92vh',
          };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={device.isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: dialogPaperSx }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: device.isMobile ? '100%' : 'auto',
                    minHeight: device.isMobile ? '100dvh' : undefined,
                }}>
                <Box
                    sx={{
                        px: 3,
                        pt: device.isMobile ? 2.5 : 3,
                        pb: 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexShrink: 0,
                    }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: device.isMobile ? 24 : 28,
                                color: IG_AUTO_GOLD,
                                lineHeight: 1.1,
                            }}>
                            Post Automation
                        </Typography>
                        <Typography sx={{ color: IG_AUTO_MUTED, fontSize: 13, mt: 0.5 }}>
                            Post to all connected platforms.
                            {instagramCommentAutomationEnabled && ' Instagram supports comment-to-DM automation.'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: IG_AUTO_MUTED }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 3,
                        pb: 2,
                    }}>
                    {projectTitle && (
                        <Typography sx={{ fontSize: 12, color: IG_AUTO_MUTED, mb: 2 }}>
                            Project:{' '}
                            <Box component="span" sx={{ color: IG_AUTO_WHITE }}>
                                {projectTitle}
                            </Box>
                        </Typography>
                    )}

                    {!canPost && (
                        <Box sx={{ ...igAutoGlassCardSx, mb: 2 }}>
                            {isMediaUploading ? (
                                <>
                                    <Typography
                                        sx={{
                                            color: IG_AUTO_WHITE,
                                            fontSize: 14,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        }}>
                                        Please wait
                                    </Typography>
                                    <Typography sx={{ color: IG_AUTO_MUTED, fontSize: 13, mt: 1, lineHeight: 1.5 }}>
                                        Your video is still uploading. Post Automation will unlock when the upload
                                        finishes.
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                mb: 0.75,
                                            }}>
                                            <Typography sx={{ fontSize: 12, color: IG_AUTO_MUTED }}>
                                                {uploadMessage || 'Uploading…'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: IG_AUTO_GOLD,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {Math.round(Number(uploadProgress) || 0)}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.min(100, Math.max(0, Number(uploadProgress) || 0))}
                                            sx={{
                                                height: 8,
                                                borderRadius: 99,
                                                bgcolor: 'rgba(255,255,255,0.08)',
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: IG_AUTO_GOLD,
                                                    borderRadius: 99,
                                                },
                                            }}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        sx={{
                                            color: IG_AUTO_WHITE,
                                            fontSize: 14,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        }}>
                                        No media available
                                    </Typography>
                                    <Typography sx={{ color: IG_AUTO_MUTED, fontSize: 13, mt: 1, lineHeight: 1.5 }}>
                                        Upload and process your video before posting to social platforms.
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}

                    {showInitialLoader ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: IG_AUTO_GOLD }} />
                        </Box>
                    ) : (
                        <>
                            <TextField
                                label="Caption (Instagram & TikTok)"
                                size="small"
                                multiline
                                minRows={3}
                                value={caption}
                                onChange={e => setCaption(e.target.value)}
                                fullWidth
                                sx={{ ...igAutoTextFieldSx, mb: 3 }}
                                helperText="Shared caption for Instagram and TikTok. YouTube uses its own title on the YouTube card. TikTok privacy, interactions, and commercial settings are on the TikTok card only."
                                FormHelperTextProps={{ sx: { color: IG_AUTO_MUTED, fontSize: 11 } }}
                            />

                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: IG_AUTO_MUTED,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.2em',
                                    fontWeight: 700,
                                    mb: 2,
                                }}>
                                Platforms
                            </Typography>

                            {PLATFORMS.map(platform => (
                                <PostAutomationPlatformCard
                                    key={platform}
                                    platform={platform}
                                    enabled={platformEnabled[platform]}
                                    onEnabledChange={v => setPlatformEnabled(prev => ({ ...prev, [platform]: v }))}
                                    connected={Boolean(connectedPlatforms[platform])}
                                    facebookUpgradeRequired={
                                        platform === 'instagram'
                                            ? needsFacebookUpgradeForInstagram(instagramRow)
                                            : false
                                    }
                                    username={connectedPlatforms[platform]}
                                    onRefreshConnections={refreshConnections}
                                    onOAuthCode={brandId ? handleBrandOAuth : undefined}
                                    authBaseUrl={brandId ? brandAuthUrl : undefined}
                                    publishStatus={platformPublish[platform].status}
                                    publishError={platformPublish[platform].error}
                                    showAutomation={platform === 'instagram' && instagramCommentAutomationEnabled}
                                    automationEnabled={automationEnabled}
                                    onAutomationEnabledChange={v => {
                                        if (v && !requireInstagramAutomationEntitlement()) return;
                                        setAutomationEnabled(v);
                                    }}
                                    automationReady={instagramReady}
                                    automationActive={platformPublish.instagram.status === 'done' && automationEnabled}
                                    onConnectAutomation={connectInstagramAutomation}
                                    connectingAutomation={saving}
                                    triggerWord={triggerWord}
                                    onTriggerWordChange={setTriggerWord}
                                    dmMessage={dmMessage}
                                    onDmMessageChange={setDmMessage}
                                    publicReply={publicReply}
                                    onPublicReplyChange={setPublicReply}
                                    sharedCaption={caption}
                                    youtube={platform === 'youtube' ? youtubeFields : undefined}
                                    onYoutubeChange={
                                        platform === 'youtube'
                                            ? patch => setYoutubeFields(prev => ({ ...prev, ...patch }))
                                            : undefined
                                    }
                                    tiktok={platform === 'tiktok' ? tiktokFields : undefined}
                                    onTiktokChange={
                                        platform === 'tiktok'
                                            ? patch => setTiktokFields(prev => ({ ...prev, ...patch }))
                                            : undefined
                                    }
                                    tiktokMediaPreviewUrl={
                                        platform === 'tiktok' && isStandalone ? standaloneMedia?.media_url : undefined
                                    }
                                    tiktokCreatorNickname={tiktokConnectedRow?.creator_nickname}
                                />
                            ))}

                            {canPost && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            color: IG_AUTO_MUTED,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2em',
                                            fontWeight: 700,
                                            mb: 2,
                                        }}>
                                        Timeline
                                    </Typography>
                                    <Box sx={{ ...igAutoGlassCardSx }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                p: 0.5,
                                                bgcolor: '#201f1f',
                                                borderRadius: 999,
                                                mb: 2,
                                            }}>
                                            {(['now', 'schedule'] as const).map(mode => (
                                                <Button
                                                    key={mode}
                                                    fullWidth
                                                    onClick={() => setScheduleMode(mode)}
                                                    sx={{
                                                        py: 1,
                                                        borderRadius: 999,
                                                        textTransform: 'none',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 13,
                                                        bgcolor: scheduleMode === mode ? IG_AUTO_GOLD : 'transparent',
                                                        color: scheduleMode === mode ? '#392700' : IG_AUTO_MUTED,
                                                    }}>
                                                    {mode === 'now' ? 'Post Now' : 'Schedule'}
                                                </Button>
                                            ))}
                                        </Box>
                                        {scheduleMode === 'schedule' ? (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                                    gap: 2,
                                                }}>
                                                <TextField
                                                    type="date"
                                                    label="Release date"
                                                    size="small"
                                                    value={scheduleDate}
                                                    onChange={e => setScheduleDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={igAutoTextFieldSx}
                                                />
                                                <TextField
                                                    type="time"
                                                    label="Release time"
                                                    size="small"
                                                    value={scheduleTime}
                                                    onChange={e => setScheduleTime(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={igAutoTextFieldSx}
                                                />
                                            </Box>
                                        ) : (
                                            <Typography
                                                sx={{ fontSize: 12, color: IG_AUTO_MINT, display: 'flex', gap: 1 }}>
                                                <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                                                Posts publish immediately to each enabled platform.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {error && (
                                <Typography sx={{ color: IG_AUTO_RED, fontSize: 13, mt: 2, lineHeight: 1.5 }}>
                                    {error}
                                </Typography>
                            )}

                            {PLATFORMS.some(p => platformPublish[p].platformPostUrl) && (
                                <Box sx={{ mt: 2 }}>
                                    {PLATFORMS.map(p => {
                                        const url = platformPublish[p].platformPostUrl;
                                        if (!url) return null;
                                        return (
                                            <Typography key={p} sx={{ fontSize: 12, color: IG_AUTO_MINT, mb: 0.5 }}>
                                                {p}:{' '}
                                                <Box
                                                    component="a"
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ color: IG_AUTO_GOLD }}>
                                                    View post
                                                </Box>
                                            </Typography>
                                        );
                                    })}
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                <Box sx={{ px: 3, pb: device.isMobile ? 3 : 3, pt: 1, flexShrink: 0 }}>
                    <Button
                        fullWidth
                        disabled={!canPost || saving || showInitialLoader}
                        onClick={handleActivate}
                        sx={{
                            py: 1.75,
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            letterSpacing: '0.08em',
                            background: canPost
                                ? `linear-gradient(90deg, ${IG_AUTO_GOLD}, #feb700)`
                                : 'rgba(255,255,255,0.06)',
                            color: canPost ? '#392700' : IG_AUTO_MUTED,
                            boxShadow: canPost ? '0 0 30px rgba(255,201,101,0.2)' : 'none',
                            '&.Mui-disabled': { background: 'rgba(255,255,255,0.06)', color: IG_AUTO_MUTED },
                        }}>
                        {saving
                            ? 'Publishing — this can take up to 2 min…'
                            : scheduledAtIso
                            ? 'Schedule posts'
                            : 'Post to platforms'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
